import React, { useEffect, useMemo, useState } from "react";

// import { useRouteMatch, Redirect, useLocation } from "react-router";
import { toast } from "react-toastify";

import PaymentPendingModal from "../grace-period/modals/PaymentPendingModal";
import MainPage from "./MainPage/MainPage";
import SampleSubmissionContext from "./SampleSubmissionContext";
import SampleExitModal from "./SampleSubmissionModal/SampleExitModal";
import "./SampleSubmission.css";
import SubmissionForm from "./SubmissionForm/SubmissionForm";
import TestDirectory from "./TestDirectory/TestDirectory";

import { canAccessSampleSubmissionTestDirectory } from "../../utils/Permissions";
import {
  parseSampleSubmissionPath,
  parseSampleSubmissionPathFromUrl,
  SAMPLE_SUBMISSION_BASE,
} from "./utils";

import { getSampleFields } from "../../actions/sampleSubmission";

function isCreateRoute(location) {
  return Boolean(location) && parseSampleSubmissionPathFromUrl(location.pathname).view === "create";
}

/** MAKE SURE ANY CHANGES MADE TO SAMPLE SUBMISSION WORK FOR/DON’T BREAK THE LOGGED OUT VERSION */
export default function SampleSubmission({
  // setActiveTab,
  // setSampleSubLink,
  // tabs,
  isLoggedOut,
  thirdPartyPayload,
  history,
  location,
  setAddressPage,
  setSubmissionNameNav,
  setSubmissionModeNav,
  setShowExitModal,
  exitModal,
  setExitModal,
  setCategory,
  setSubmissionFormOpen,
  setHideBreadcrumbBack,
}) {
  /** Grab type (environment or product) from the url */
  const thirdParty = thirdPartyPayload.thirdParty ?? false;
  const mrl = thirdPartyPayload.mrl === "true";

  const [showSubmissionForm, setShowSubmissionForm] = useState(() => isLoggedOut || isCreateRoute(location));
  const [draftEditing, setDraftEditing] = useState(null);
  const [submissionEditingSample, setSubmissionEditingSample] = useState(null);
  const [submissionAddingSamples, setSubmissionAddingSamples] = useState(null);
  const [fieldsError, setFieldsError] = useState(false);
  const [sampleFields, setSampleFields] = useState([]);
  const [sampleFieldsInfo, setSampleFieldsInfo] = useState({});
  const [delimiterRegex, setDelimiterRegex] = useState("");
  const [sampleIdFields, setSampleIdFields] = useState(new Set());
  const [sampleCategory, setSampleCategory] = useState(() => (
    location ? parseSampleSubmissionPath(location.pathname, thirdPartyPayload).category || "" : ""
  ));
  const [sampleFieldLoaded, setSampleFieldLoaded] = useState(false);
  const [loadingFields, setLoadingFields] = useState(false);

  const pathInfo = useMemo(
    () => (location ? parseSampleSubmissionPath(location.pathname, thirdPartyPayload) : { view: "other" }),
    [location, thirdPartyPayload],
  );
  const showTestDirectory = !isLoggedOut && !thirdParty && pathInfo.view === "directory" && canAccessSampleSubmissionTestDirectory();

  useEffect(() => {
    if (isLoggedOut || thirdParty || !history || pathInfo.view !== "directory") return;
    if (!canAccessSampleSubmissionTestDirectory()) {
      history.replace(SAMPLE_SUBMISSION_BASE);
    }
  }, [isLoggedOut, thirdParty, history, pathInfo.view]);

  useEffect(() => {
    if ((isLoggedOut && !thirdParty) || !location) return;

    if (pathInfo.view === "list") {
      setShowSubmissionForm(false);
      setSampleFieldLoaded(false);
      setDraftEditing(null);
      setSubmissionEditingSample(null);
      setSubmissionAddingSamples(null);
      if (typeof setSubmissionNameNav === "function") {
        setSubmissionNameNav("");
      }
      if (typeof setSubmissionModeNav === "function") {
        setSubmissionModeNav("add");
      }
    } else if (pathInfo.view === "create") {
      setShowSubmissionForm(true);
      setSampleCategory(pathInfo.category);
      if (typeof setSubmissionModeNav === "function") {
        setSubmissionModeNav("add");
      }
    } else if (pathInfo.view === "directory") {
      setShowSubmissionForm(false);
      setSampleFieldLoaded(false);
      setDraftEditing(null);
      setSubmissionEditingSample(null);
      setSubmissionAddingSamples(null);
      if (typeof setSubmissionNameNav === "function") {
        setSubmissionNameNav("");
      }
    }
  }, [isLoggedOut, location, pathInfo, thirdParty, setSubmissionNameNav, setSubmissionModeNav]);

  useEffect(() => {
    if (typeof setSubmissionFormOpen === "function") {
      setSubmissionFormOpen(showSubmissionForm && sampleFieldLoaded);
    }
  }, [showSubmissionForm, sampleFieldLoaded, setSubmissionFormOpen]);

  /** Get sample fields, used for sample creating form */
  useEffect(() => {
    async function apiGetSampleFields(category) {
      setLoadingFields(true);
      try {
        const result = await getSampleFields(category);
        if (result.success) {
          if (result.fields && result.fields.length) {
            const fieldsMap = {};
            result.fields.forEach((fieldObj) => {
              fieldsMap[fieldObj.json_field] = fieldObj;
            });
            /** Only show fields that are not display "2", keep the order sent by backend */
            const sampleSubmissionFields = result.fields
              .filter(({ display }) => display !== "2")
              .map(({ json_field }) => json_field);
            setSampleFieldsInfo(fieldsMap || {});
            setSampleFields(sampleSubmissionFields);
            setSampleIdFields(new Set(result.sampleid_fields ?? []));
            setDelimiterRegex(result.delimiterRegex);
            setSampleFieldLoaded(true);
          } else {
            toast.error("Fields are missing or empty");
            setFieldsError(true);
            setSampleFieldLoaded(false);
          }
        } else {
          toast.error(result.message);
          setFieldsError(true);
          setSampleFieldLoaded(false);
        }
      } catch (e) {
        toast.error(e.response?.data?.message || "Something went wrong.");
        setFieldsError(true);
        setSampleFieldLoaded(false);
      } finally {
        setLoadingFields(false);
      }
    }
    if (isLoggedOut && !thirdParty) {
      setSampleFieldsInfo({
        sample_id: {
          title_field: "Sample ID",
          display: "1",
        },
        sample_type: {
          title_field: "Sample Type",
          display: "1",
        },
      });
      setSampleFields(["sample_id", "sample_type"]);
      setSampleIdFields(new Set(["sample_id"]));
      setSampleFieldLoaded(true);
    } else if (sampleCategory !== "" && showSubmissionForm) {
      apiGetSampleFields(sampleCategory);
      setCategory(sampleCategory);
    }
  }, [sampleCategory, showSubmissionForm]); // eslint-disable-line

  /**
   * set the exit modal for editing
   */
  useEffect(() => {
    setShowExitModal(true);
  }, [exitModal]); // eslint-disable-line
  /**
   * SubmissionFrom navbar exit and back functionality edit for the dra
   */
  useEffect(() => {
    if (draftEditing) {
      setSampleCategory(draftEditing.sample_category);
      setShowExitModal(false);
    }
  }, [draftEditing]); // eslint-disable-line

  const handleGoBackToMainPage = async () => {
    if (history) history.push(SAMPLE_SUBMISSION_BASE);
    if (isLoggedOut) {
      setShowSubmissionForm(false); // go back to main page
      setSampleFieldLoaded(false);
      setSubmissionEditingSample(null);
      setDraftEditing(null);
      setSubmissionAddingSamples(null);
    }
  };

  let pageContent;
  if (showTestDirectory) {
    pageContent = <TestDirectory history={history} location={location} />;
  } else if (showSubmissionForm && sampleFieldLoaded) {
    pageContent = (
      <SubmissionForm
        setShowSubmissionForm={setShowSubmissionForm}
        setSampleFieldLoaded={setSampleFieldLoaded}
        draftEditing={draftEditing}
        submissionEditingSample={submissionEditingSample}
        setSubmissionEditingSample={setSubmissionEditingSample}
        submissionAddingSamples={submissionAddingSamples}
        setSubmissionAddingSamples={setSubmissionAddingSamples}
        setDraftEditing={setDraftEditing}
        sampleFields={sampleFields}
        sampleFieldsInfo={sampleFieldsInfo}
        delimiterRegex={delimiterRegex}
        sampleIdFields={sampleIdFields}
        sampleCategory={sampleCategory}
        setAddressPage={setAddressPage}
        setSubmissionNameNav={setSubmissionNameNav}
        setSubmissionModeNav={setSubmissionModeNav}
        setShowExitModal={setShowExitModal}
        setHideBreadcrumbBack={setHideBreadcrumbBack}
      />
    );
  } else if (thirdParty && showSubmissionForm) {
    pageContent = null;
  } else {
    pageContent = (
      <MainPage
        setShowSubmissionForm={setShowSubmissionForm}
        setSampleFieldLoaded={setSampleFieldLoaded}
        setDraftEditing={setDraftEditing}
        setSubmissionEditingSample={setSubmissionEditingSample}
        setSubmissionAddingSamples={setSubmissionAddingSamples}
        fieldsError={fieldsError}
        setSampleCategory={setSampleCategory}
        loadingFields={loadingFields}
      />
    );
  }

  return (
    <SampleSubmissionContext.Provider value={{
      isLoggedOut, sampleCategory, thirdParty, setSampleCategory, mrl,
    }}
    >
      {exitModal && <SampleExitModal bodyText="Are you sure you want to leave without saving?" headerText="Unsaved Changes" setExitModal={setExitModal} handleGoBackToMainPage={handleGoBackToMainPage} />}
      {!isLoggedOut && !thirdParty && showSubmissionForm && sampleFieldLoaded && (
        <PaymentPendingModal onGoBack={handleGoBackToMainPage} />
      )}
      {pageContent}
    </SampleSubmissionContext.Provider>
  );
}
